import { yupResolver } from '@hookform/resolvers/yup';
import { typographyClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { styled } from '@mui/system';
import { ModelError_Item, SiteType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { camelCase, sortBy } from 'lodash-es';
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { CreateOptionChip } from '~components/FormFields/CreateOptionChip';
import { TextFormField } from '~components/FormFields/TextFormField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import NumberInput from '~components/FormFields/v2/NumberInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { AddressOption, AddressType } from '~components/Order/SiteSelection/types';
import { FormStateChangeProps } from '~formsShared/types';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import {
  Equipment,
  EquipmentTypeItem,
  maxGrossWeightToNumber,
} from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { BasicLabel } from '~hooks/useLabels/models';
import useLabels from '~hooks/useLabels/useLabels';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { useSites } from '~hooks/useSites/useSites';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import OwnedByField, {
  ACTING_COMPANY_OWNER_VALUE,
} from '~pages/Settings/Administration/EquipmentManagement/components/OwnedByField';
import {
  equipmentFormValidationSchema,
  sharedEquipmentFormValidationSchema,
} from '~pages/Settings/Administration/EquipmentManagement/equipmentFormValidationSchema';
import useVetaCenterConnectionQuery from '~queries/useVetaCenterConnectionQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

export type EquipmentFormValues = yup.InferType<typeof equipmentFormValidationSchema>;

export interface EquipmentFormHandlers {
  submit: (callBack: (data: EquipmentFormValues) => void) => void;
  resetForm: (callBack?: () => void) => void;
}

interface EquipmentFormProps {
  defaultEquipment: Nullable<Equipment>;
  companyShares: Nullable<Array<CompanyShare>>;
  onFormStateChange: ({ isValid, isDirty }: FormStateChangeProps) => void;
  onFormDirty?: (isDirty: boolean) => void;
  selectedCompanyId: Nullable<string>;
  errors?: ModelError_Item[];
  isManagedEquipment?: boolean;
  managedCompanyId?: string;
  onCompanyChange?: (companyId: string) => void;
}

export const EquipmentHint = styled(Typography)<TypographyProps>(({ theme }) => {
  return {
    fontSize: '12px',
    mt: 0.5,
    color: theme.palette.text.secondary,
  };
});

const EquipmentForm = forwardRef(function EquipmentForm(
  {
    defaultEquipment,
    companyShares,
    onFormStateChange,
    selectedCompanyId,
    errors: errorsProp,
    isManagedEquipment = false,
    managedCompanyId,
    onCompanyChange,
  }: EquipmentFormProps,
  ref: Ref<EquipmentFormHandlers>,
) {
  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState<EquipmentTypeItem[]>(
    [],
  );
  const [connectedCompanies, setConnectedCompanies] = useState<CompanyBasic[]>([]);
  const { userStore, toasterStore } = useStores();
  const {
    getAllConnectedCompaniesByCompanyId,
    getAllConnectedCompaniesByCompanyIdTypeahead,
  } = useCompany();
  const isSharedEquipment = !!defaultEquipment?.companyShare?.id;
  const selectedCompanyOwnsEquipment =
    defaultEquipment?.company?.id === selectedCompanyId;
  const isProvisionedEquipment =
    !!defaultEquipment?.internalCompany?.id &&
    defaultEquipment.internalCompany.id !== defaultEquipment?.company?.id;
  const { getEquipmentTypesByCompanyId, createEquipmentTypeByCompanyId } = useEquipment();
  const issueEquipmentTypesSignal = useLatestRequestSignal();
  const [isEquipmentTypeLoading, setIsEquipmentTypeLoading] = useState(false);
  const { fetchLabelsTypeahead } = useLabels();

  const { getCompanySitesTypeahead } = useSites();
  const { getPlaces, getAddressItemByGoogleMapsPlaceId } = useGeocode({
    addressProvider: SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH,
  });

  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
  // Fetch sites and Google suggestions for home base selector
  const fetchSitesAndSuggestions = async ({ query }: { query: string }) => {
    // Fetch company sites filtered to EquipmentHomeBase type via API
    const { data: sites = [] } = await getCompanySitesTypeahead({
      companyId: managedCompanyId
        ? effectiveCompanyId
        : (selectedCompany?.id ?? effectiveCompanyId),
      query,
      onlyActiveSites: true,
      siteTypes: [SiteType.EQUIPMENT_HOME_BASE],
    });

    const siteOptions = sites.map((site) => ({
      type: AddressType.Sites,
      id: site.id,
      siteId: site.id,
      name: site.name,
      lat: String(site.lat ?? ''),
      lng: String(site.lon ?? ''),
    }));

    // Fetch Google suggestions
    const places = (await getPlaces({ query })) || [];
    const suggestionOptions = places.slice(0, 5).map((place) => ({
      type: AddressType.Suggestions,
      id: place.placeId,
      siteId: null,
      name: place.nameWithAddress,
      lat: String(place.lat ?? ''),
      lng: String(place.lng ?? ''),
      placeId: place.placeId,
      address: place,
    }));

    return { data: [...siteOptions, ...suggestionOptions] };
  };

  // Handle selection of home base option (resolve Google suggestions)
  const handleHomeBaseSelect = async (item: Partial<AddressOption> | null) => {
    if (item?.type === AddressType.Suggestions && item.placeId) {
      const addressItem = await getAddressItemByGoogleMapsPlaceId(
        item.placeId,
        item.address?.fullAddress ?? undefined,
      );
      return {
        ...item,
        lat: String(addressItem.lat ?? ''),
        lng: String(addressItem.lng ?? ''),
        address: addressItem,
      };
    }
    return item;
  };

  const defaultCompany =
    defaultEquipment?.company || userStore.currentCompanies[0] || userStore.user.company;
  const actingCompanyName = defaultCompany?.legalName ?? '';
  const ownerTypeaheadCompanyId = selectedCompanyId ?? userStore.userCompany?.id;
  const showOwnedByField =
    !managedCompanyId &&
    !isSharedEquipment &&
    !isProvisionedEquipment &&
    !!ownerTypeaheadCompanyId;

  const defaultValues = {
    id: defaultEquipment?.id || '',
    name: defaultEquipment?.name || '',
    company: defaultCompany,
    type: {
      name: defaultEquipment?.type?.name || '',
      id: defaultEquipment?.type?.id || '',
      system: defaultEquipment?.type?.system || false,
    },

    externalId: defaultEquipment?.externalId || '',
    licenseNumber: defaultEquipment?.licenseNumber || '',
    accountingId: defaultEquipment?.accountingId || '',
    zone: defaultEquipment?.zone || '',
    overweightPermitNumber: defaultEquipment?.overweightPermitNumber ?? '',
    maxGrossWeight: maxGrossWeightToNumber(defaultEquipment?.maxGrossWeight ?? null),
    homeBase: defaultEquipment?.homeBase
      ? {
          type: AddressType.Sites,
          id: defaultEquipment.homeBase.id,
          siteId: defaultEquipment.homeBase.id,
          name: defaultEquipment.homeBase.name,
          lat: String(defaultEquipment.homeBase.lat ?? ''),
          lng: String(defaultEquipment.homeBase.lon ?? ''),
          address: null,
        }
      : null,
    isNotActive: defaultEquipment?.isNotActive || false,
    ownedByAccount: { label: actingCompanyName, value: ACTING_COMPANY_OWNER_VALUE },
    shareName: '',
    shareTicketMatchId: '',
    companySpecificId: defaultEquipment?.companyShare?.externalId || '',
    labels: defaultEquipment?.labels ?? [],
    companyShares: companyShares?.length
      ? companyShares.map((companyShare) => {
          return {
            id: companyShare.id,
            receiverCompany: {
              id: companyShare.receiverCompany?.id,
              legalName: companyShare.receiverCompany?.legalName,
            },
            dispatchable: {
              id: companyShare.dispatchable ? 'dispatchable' : 'view_only',
              name: companyShare.dispatchable
                ? t('common.dispatchable')
                : t('common.view_only'),
            },
            ticketMatchId: companyShare.ticketMatchId || '',
            companyShareableId: companyShare.companyShareable.id,
          };
        })
      : [],
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty },
    reset,
    resetField,
    setError,
    clearErrors,
  } = useForm<EquipmentFormValues>({
    resolver: yupResolver(
      isSharedEquipment
        ? sharedEquipmentFormValidationSchema
        : equipmentFormValidationSchema,
    ),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  // Set errors from props
  useEffect(() => {
    if (errorsProp) {
      for (const error of errorsProp) {
        const field = camelCase(error.field) as keyof ReturnType<typeof setError>;
        // Replace external ID with Equipment ID
        const message = error.message.replace('external', 'Equipment');

        if (field && message) {
          setError(field, { type: 'manual', message });
        }
      }
    }

    return () => {
      clearErrors();
    };
  }, [errorsProp]);

  const selectedCompany = watch('company');
  const homeBase = watch('homeBase');
  const { isVetaCenterConnected } = useVetaCenterConnectionQuery({
    companyId: managedCompanyId ?? selectedCompany?.id,
  });

  useEffect(() => {
    if (!isVetaCenterConnected) {
      resetField('overweightPermitNumber');
      resetField('maxGrossWeight');
    }
  }, [isVetaCenterConnected, resetField]);

  useEffect(() => {
    if (selectedCompany?.id) {
      onCompanyChange?.(selectedCompany.id);
    }
  }, [selectedCompany?.id, onCompanyChange]);

  // Update default values once async companyShares are loaded
  useEffect(() => {
    if (!isManagedEquipment) {
      reset(defaultValues);
    }
  }, [companyShares, isManagedEquipment]);

  const {
    fields: companySharesFields,
    append: appendCompanyShares,
    remove: removeCompanyShares,
  } = useFieldArray({
    control,
    name: 'companyShares',
  });

  const [tmpEquipmentType, setTmpEquipmentType] = useState<string>('');
  const onInputEquipmentType = (event: React.SyntheticEvent, value: string) => {
    setTmpEquipmentType(value);
  };

  const createEquipmentType = async () => {
    const companyId = managedCompanyId
      ? effectiveCompanyId
      : selectedCompanyId || userStore.userCompany?.id;

    if (!companyId) {
      toasterStore.push(
        alert(t('form_validation_errors.no_user_company'), AlertTypes.error),
      );

      return;
    }

    setIsEquipmentTypeLoading(true);
    try {
      const equipmentType = await createEquipmentTypeByCompanyId({
        companyId,
        managedCompanyId,
        equipmentType: { name: tmpEquipmentType } as unknown as EquipmentTypeItem,
      });

      setEquipmentTypesOptions((prevState) => [...prevState, equipmentType]);
      setValue('type', equipmentType);
      trigger('type');
      toasterStore.push(
        alert(
          t('administration.equipment_type.created_message', {
            name: equipmentType.name,
          }),
          AlertTypes.success,
        ),
      );
    } catch {
      // Error is already handled in the hook
    } finally {
      setIsEquipmentTypeLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    submit(callBack: (data: EquipmentFormValues) => void) {
      handleSubmit(callBack)();
    },
    resetForm(callBack?: () => void) {
      reset();
      callBack?.();
    },
  }));

  const previousCompanyId = usePrevious(selectedCompany?.id);

  useEffect(() => {
    if (
      selectedCompany?.id &&
      !isSharedEquipment &&
      previousCompanyId !== selectedCompany?.id
    ) {
      if (previousCompanyId) {
        // A vendor connected to the old company is not a valid owner under the new one.
        setValue('ownedByAccount', {
          label: selectedCompany.legalName ?? '',
          value: ACTING_COMPANY_OWNER_VALUE,
        });
        setValue('shareName', '');
        setValue('shareTicketMatchId', '');
      }

      const signal = issueEquipmentTypesSignal();
      const equipmentTypesCompanyId = managedCompanyId
        ? effectiveCompanyId
        : selectedCompany.id;
      const loadEquipmentTypes = async () => {
        setIsEquipmentTypeLoading(true);

        try {
          const { data } = await getEquipmentTypesByCompanyId({
            companyId: equipmentTypesCompanyId,
            managedCompanyId,
            signal,
          });
          if (signal.aborted) return;

          setEquipmentTypesOptions(sortBy(data, 'name'));
        } catch {
          // Error is already handled in the hook
        } finally {
          if (!signal.aborted) {
            setIsEquipmentTypeLoading(false);
          }
        }
      };

      void loadEquipmentTypes();
    }
  }, [selectedCompany, isSharedEquipment, previousCompanyId, managedCompanyId, setValue]);

  useEffect(() => {
    const companyId = managedCompanyId
      ? effectiveCompanyId
      : selectedCompanyId || userStore?.userCompany?.id;

    if (companyId) {
      getAllConnectedCompaniesByCompanyId({
        companyId,
        managedCompanyId,
        callback: setConnectedCompanies,
      });
    }
  }, [userStore?.userCompany?.id, selectedCompanyId, managedCompanyId]);

  useEffect(() => {
    onFormStateChange({ isValid, isDirty });
  }, [isValid, isDirty]);

  return (
    <form>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flexGrow: 1,
        }}
      >
        {!isManagedEquipment && !managedCompanyId && (
          <Box sx={{ width: '100%' }}>
            <CompanySelectorFormField
              sx={{ width: '100%', minWidth: '100%' }}
              name={'company'}
              isRequired={true}
              disabled={!!defaultEquipment?.company?.id || isSharedEquipment}
              errors={errors}
              control={control}
            />
          </Box>
        )}
        {defaultEquipment?.equipmentId && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {t('form_fields.tread_id')}
            </Typography>
            <Chip
              label={defaultEquipment.equipmentId}
              color="default"
              variant="outlined"
              size="small"
              sx={{
                borderRadius: 1,
                fontSize: '12px',
                fontWeight: 700,
              }}
            />
          </Box>
        )}

        {showOwnedByField && (
          <OwnedByField
            actingCompanyName={selectedCompany?.legalName ?? actingCompanyName}
            companyId={selectedCompany?.id ?? ownerTypeaheadCompanyId}
            control={control}
            errors={errors}
            setValue={setValue}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            mt: 2,
          }}
        >
          <Box sx={{ width: '50%', mr: 2 }}>
            <TextFormField
              control={control}
              errors={errors}
              name="name"
              label={`${t('form_fields.truck_number_name')}`}
              isRequired={true}
              disabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
            }}
          >
            <AutocompleteFormField
              control={control}
              name="type"
              disabled={
                !selectedCompany?.id ||
                (isSharedEquipment && !selectedCompanyOwnsEquipment)
              }
              errors={errors}
              list={equipmentTypesOptions || []}
              label={`${t('form_fields.equipment_type')}`}
              isRequired={true}
              getValue={(item) => item?.id}
              getLabel={(item) => item.name}
              placeholder={t('form_fields.select')}
              onInput={onInputEquipmentType}
              noOptionsText={
                tmpEquipmentType ? (
                  <CreateOptionChip
                    disabled={isEquipmentTypeLoading}
                    name={tmpEquipmentType || ''}
                    onClick={createEquipmentType}
                  />
                ) : (
                  t('form_fields.no_options')
                )
              }
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            mt: 2,
          }}
        >
          <Box sx={{ width: '50%', mr: 2 }}>
            <TextFormField
              control={control}
              errors={errors}
              name="externalId"
              label={`${t('form_fields.equipment_id')}`}
              isRequired={false}
              disabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              width: '50%',
            }}
          >
            <TextFormField
              control={control}
              errors={errors}
              name="licenseNumber"
              label={`${t('form_fields.license_number')}`}
              isRequired={false}
              disabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            mt: 2,
          }}
        >
          <Box sx={{ width: '50%', mr: 2 }}>
            <TextFormField
              control={control}
              errors={errors}
              name="accountingId"
              label={`${t('administration.equipment.accounting_id')}`}
              isRequired={false}
              disabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
            />
          </Box>
          <Box sx={{ width: '50%' }}>
            <TextFormField
              control={control}
              errors={errors}
              name="zone"
              label={`${t('administration.equipment.zone')}`}
              isRequired={false}
              disabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
            />
          </Box>
        </Box>
        {isVetaCenterConnected && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Box sx={{ width: '50%' }}>
              <TextInput
                control={control}
                isDisabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
                label={t('administration.equipment.overweight_permit_number')}
                name="overweightPermitNumber"
              />
            </Box>
            <Box sx={{ width: '50%' }}>
              <NumberInput
                control={control}
                isDisabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
                label={t('administration.equipment.max_gross_weight')}
                name="maxGrossWeight"
              />
            </Box>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <AutocompleteAsyncFormField
            control={control}
            errors={errors}
            name="homeBase"
            getValue={(item) => item?.id ?? ''}
            getLabel={(item) => item?.name ?? ''}
            asyncCallback={fetchSitesAndSuggestions}
            onSelect={handleHomeBaseSelect}
            label={t('administration.equipment.home_base')}
            placeholder={t('administration.equipment.search_site_or_address')}
            clearable
            disabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
            groupBy={(option) =>
              option.type === AddressType.Sites
                ? t('common.sites')
                : t('common.suggested_addresses')
            }
            disablePortal={false}
          />
          {!homeBase && (
            <EquipmentHint sx={{ mt: 0.5 }}>
              {t('administration.equipment.no_home_base_message')}
            </EquipmentHint>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <AutocompleteAsyncFormField
            control={control}
            errors={errors}
            name="labels"
            multiple
            getValue={(item: BasicLabel) => item.id}
            getLabel={(item: BasicLabel) => item.name}
            asyncCallback={fetchLabelsTypeahead}
            extraRequestOptions={{
              companyId: selectedCompanyId ?? userStore.userCompany?.id ?? '',
            }}
            label={`${t('form_fields.labels')}`}
            disabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
          />
        </Box>
        <Box>
          <CheckboxInput
            control={control}
            isDisabled={isSharedEquipment && !selectedCompanyOwnsEquipment}
            label={t('common.inactive')}
            name="isNotActive"
            sx={{
              mt: 2,
              [`& .${typographyClasses.root}`]: { fontSize: '13px', fontWeight: 400 },
            }}
          />
        </Box>
        {
          <>
            <Box>
              {!isProvisionedEquipment && (
                <Box>
                  <Typography variant={'h6'} sx={{ mt: 2 }}>
                    {t('administration.equipment.share_with_connected_companies')}
                  </Typography>
                  <Box>
                    <EquipmentHint variant={'body2'} mt={0.5}>
                      {t('administration.equipment.share_with_connected_companies_desc')}
                    </EquipmentHint>
                  </Box>
                </Box>
              )}
              {isSharedEquipment && !selectedCompanyOwnsEquipment && (
                <>
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      flexDirection: 'column',
                      mt: 1,
                      border: `1px solid ${theme.semantic.borderStrong}`,
                      padding: '5px',
                    })}
                  >
                    <EquipmentHint variant={'body2'}>
                      {t('administration.equipment.shared_from_company')}:{' '}
                      {defaultEquipment?.companyShare?.senderCompany?.legalName}
                    </EquipmentHint>
                    <EquipmentHint variant={'body2'}>
                      {t('administration.equipment.shared_from_ext_id')}:{' '}
                      {defaultEquipment?.externalId}
                    </EquipmentHint>
                    <EquipmentHint variant={'body2'}>
                      {t('administration.equipment.shared_from_access')}:{' '}
                      {defaultEquipment?.companyShare?.dispatchable
                        ? t('common.dispatchable')
                        : t('common.view_only')}
                    </EquipmentHint>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      mt: 1.5,
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '50%',
                        mr: 2,
                      }}
                    >
                      <TextFormField
                        control={control}
                        errors={errors}
                        name="companySpecificId"
                        label={`${t('form_fields.company_specific_id')}`}
                        isRequired={false}
                      />
                    </Box>
                    <EquipmentHint variant={'body2'} sx={{ width: '50%' }}>
                      {t('administration.equipment.input_company_specific_id')}
                    </EquipmentHint>
                  </Box>
                </>
              )}

              {/* Company Shares */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {companySharesFields.map((item, index) => {
                  return (
                    <Box
                      key={item.id}
                      sx={{ display: 'flex', gap: 1, width: '100%', mt: 2 }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <AutocompleteAsyncFormField
                          control={control}
                          errors={errors}
                          name={`companyShares[${index}].receiverCompany`}
                          getValue={(item) => item.id}
                          getLabel={(item) => item.legalName}
                          asyncCallback={getAllConnectedCompaniesByCompanyIdTypeahead}
                          extraRequestOptions={{
                            companyId: managedCompanyId
                              ? effectiveCompanyId
                              : (selectedCompanyId ?? userStore.userCompany.id ?? ''),
                            managedCompanyId,
                          }}
                          label={`${t(
                            isProvisionedEquipment
                              ? 'administration.equipment.owned_by'
                              : 'form_fields.share_to_company',
                          )}`}
                          disabled={
                            !!(
                              defaultValues.companyShares.length &&
                              item.receiverCompany?.id
                            )
                          }
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <TextFormField
                          control={control}
                          errors={errors}
                          name={`companyShares[${index}].ticketMatchId`}
                          label={`${t('form_fields.ticket_match_number')}`}
                          isRequired={false}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <AutocompleteFormField
                          control={control}
                          errors={errors}
                          name={`companyShares[${index}].dispatchable`}
                          list={[
                            {
                              id: 'dispatchable',
                              name: t('common.dispatchable'),
                            },
                            {
                              id: 'view_only',
                              name: t('common.view_only'),
                            },
                          ]}
                          isRequired={false}
                          getValue={(item) => item.id}
                          getLabel={(item) => item.name}
                          label={`${t('form_fields.access_level')}`}
                          disabled={
                            isSharedEquipment &&
                            !selectedCompanyOwnsEquipment &&
                            !defaultEquipment?.companyShare?.dispatchable
                          }
                        />
                      </Box>
                      <RemoveItemButton
                        action={() => removeCompanyShares(index)}
                        sx={{ cursor: 'pointer', mt: 2.3 }}
                      />
                    </Box>
                  );
                })}
              </Box>

              {!isProvisionedEquipment && (
                <PseudoLink
                  disabled={companySharesFields.length === connectedCompanies.length}
                  title={`+ ${t('administration.equipment.add_shared_company')}`}
                  sx={{
                    mt: 2,
                    display: 'inline-flex',
                    pointerEvents:
                      companySharesFields.length === connectedCompanies.length
                        ? 'none'
                        : 'auto',
                  }}
                  action={() => {
                    companySharesFields.length !== connectedCompanies.length &&
                      appendCompanyShares({
                        id: '',
                        receiverCompany: { id: '', legalName: '' },
                        dispatchable: { id: 'view_only', name: 'View Only' },
                        ticketMatchId: '',
                        companyShareableId: defaultEquipment?.id || '',
                      });
                  }}
                />
              )}
            </Box>
          </>
        }
      </Box>
    </form>
  );
});
export { EquipmentForm };
